import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { Alert, Form, Input, InputNumber, Modal, Radio, Select, message } from "antd";
import { listReturnAddressAreas, saveShopReturnAddress } from "@/api/shopReturnAddress";
import type { ReturnAddressArea, ShopReturnAddress, ShopReturnAddressWrite } from "@/api/shopReturnAddress";
import { phoneReg } from "@/utils/regs";
import { nonNegativeRules } from "../numericValidation";

interface ReturnAddressEditorProps {
  address: ShopReturnAddress | null;
  onCancel: () => void;
  onSuccess: () => void;
}
interface AreaOptions { rows: ReturnAddressArea[]; loading: boolean; failed: boolean }
function useAreas(parentId: number | undefined): AreaOptions {
  const [options, setOptions] = useState<AreaOptions>({ rows: [], loading: false, failed: false });
  useEffect(() => {
    const controller = new AbortController();
    setOptions({ rows: [], loading: parentId !== undefined, failed: false });
    if (parentId !== undefined) {
      void listReturnAddressAreas(parentId, controller.signal).then(({ data }) => {
        if (!controller.signal.aborted) setOptions({ rows: data ?? [], loading: false, failed: false });
      }).catch(() => {
        if (!controller.signal.aborted) setOptions({ rows: [], loading: false, failed: true });
      });
    }
    return (): void => controller.abort();
  }, [parentId]);
  return options;
}

export function ReturnAddressEditor({ address, onCancel, onSuccess }: ReturnAddressEditorProps): ReactElement {
  const [form] = Form.useForm<ShopReturnAddressWrite>();
  const province: number | undefined = Form.useWatch("province", form);
  const city: number | undefined = Form.useWatch("city", form);
  // 与渠道管理一致：100000 是中国节点，省份为其直接子级，不能从根节点 0 开始。
  const provinces = useAreas(100000);
  const cities = useAreas(province);
  const districts = useAreas(city);
  const [busy, setBusy] = useState(false);
  const submitting = useRef(false);
  const handleProvinceChange = (): void => { form.setFieldsValue({ city: undefined, district: undefined }); };
  const handleCityChange = (): void => { form.setFieldsValue({ district: undefined }); };
  const handleCancel = (): void => { if (!submitting.current) onCancel(); };
  const handleSave = async (values: ShopReturnAddressWrite): Promise<void> => {
    if (submitting.current) return;
    submitting.current = true;
    setBusy(true);
    try {
      await saveShopReturnAddress({
        ...(address ? { id: address.id } : {}),
        receiverName: values.receiverName.trim(), receiverPhone: values.receiverPhone.trim(),
        province: values.province, city: values.city, district: values.district,
        detailAddress: values.detailAddress.trim(), isDefault: values.isDefault, sort: values.sort, status: values.status,
      });
      void message.success("保存成功");
      onSuccess();
    } catch { /* 请求层提示错误，保留输入以便重试。 */ }
    finally { submitting.current = false; setBusy(false); }
  };
  const areaOptions = (options: AreaOptions, id?: number, name?: string): { value: number; label: string }[] => {
    const rows = options.rows.map((area) => ({ value: area.id, label: area.name }));
    if (id !== undefined && !rows.some((area) => area.value === id)) rows.push({ value: id, label: name || String(id) });
    return rows;
  };
  return <Modal open title={`${address ? "编辑" : "新增"}退货地址`} onCancel={handleCancel} onOk={() => form.submit()}
    okText="保存" cancelText="取消" confirmLoading={busy} maskClosable={false} closable={!busy} keyboard={!busy} cancelButtonProps={{ disabled: busy }}>
    <Form form={form} layout="vertical" initialValues={address ?? { isDefault: 0, sort: 0, status: 1 }} onFinish={handleSave} disabled={busy}>
      <Form.Item name="receiverName" label="收件人" rules={[{ required: true, whitespace: true, message: "请输入收件人" }, { max: 32, message: "收件人不能超过32字" }]}><Input placeholder="请输入收件人姓名" /></Form.Item>
      <Form.Item name="receiverPhone" label="收件人电话" rules={[{ required: true, whitespace: true, message: "请输入收件人电话" }, { pattern: phoneReg, message: "请输入正确的11位手机号" }]}><Input type="tel" placeholder="请输入11位手机号" /></Form.Item>
      {(provinces.failed || cities.failed || districts.failed) && <Alert type="error" message="地区加载失败，请重新打开弹窗重试" style={{ marginBottom: 12 }} />}
      <Form.Item name="province" label="省份" rules={[{ required: true, message: "请选择省份" }]}>
        <Select showSearch optionFilterProp="label" placeholder="请选择省份" loading={provinces.loading} options={areaOptions(provinces, address?.province, address?.provinceName)} onChange={handleProvinceChange} />
      </Form.Item>
      <Form.Item name="city" label="城市" rules={[{ required: true, message: "请选择城市" }]}>
        <Select showSearch optionFilterProp="label" placeholder="请选择城市" disabled={busy || province === undefined} loading={cities.loading}
          options={areaOptions(cities, province === address?.province ? address?.city : undefined, address?.cityName)} onChange={handleCityChange} />
      </Form.Item>
      <Form.Item name="district" label="区县" rules={[{ required: true, message: "请选择区县" }]}>
        <Select showSearch optionFilterProp="label" placeholder="请选择区县" disabled={busy || city === undefined} loading={districts.loading}
          options={areaOptions(districts, city === address?.city ? address?.district : undefined, address?.districtName)} />
      </Form.Item>
      <Form.Item name="detailAddress" label="详细地址" rules={[{ required: true, whitespace: true, message: "请输入详细地址" }, { max: 255, message: "详细地址不能超过255字" }]}><Input.TextArea rows={3} placeholder="请输入街道、门牌号等详细地址" /></Form.Item>
      <Form.Item name="isDefault" label="默认地址"><Radio.Group options={[{ label: "是", value: 1 }, { label: "否", value: 0 }]} /></Form.Item>
      <Form.Item name="status" label="状态" rules={[{ required: true }]}><Radio.Group options={[{ label: "正常", value: 1 }, { label: "停用", value: 0 }]} /></Form.Item>
      <Form.Item name="sort" label="排序" rules={nonNegativeRules("排序", true, true)}><InputNumber min={0} precision={0} /></Form.Item>
    </Form>
  </Modal>;
}
